/**
 * Single-incumbent senior roles — key-person risk (pack slide 30).
 *
 * The framing is the panel: these are senior roles resting on ONE employee, with
 * no second position carrying the same role behind them. It is a STRUCTURAL risk
 * list and nothing more — the platform holds no succession data, so nothing
 * here says whether a successor exists, is ready, or has ever been discussed.
 * That sentence is on the panel's face rather than left to be assumed.
 *
 * Identity serves the minimum set today: the count, the bands, the executive
 * list, band D by function, band C and the 60+ figure. Critical roles and
 * occupational levels are not served yet, so this panel neither renders nor is
 * titled by them.
 *
 * THREE THINGS THIS PANEL REFUSES TO DO
 *
 *  1. TREAT A WITHHELD COUNT AS A ZERO. A null band or function row is drawn as
 *     a hatched track with a dash — never a zero-length bar reading "0" — and it
 *     never reaches a sum, a maximum or a percentage. The 60+ tile tells withheld
 *     (fewer than five) from not available for this date (a senior employee has
 *     no birth date on record) by `sixty_plus_withheld`, never by guessing.
 *  2. BACK THE WITHHELD CELLS OUT OF THE TOTAL. The server suppresses
 *     complementarily: where only one cell was small it withholds the
 *     next-smallest too, precisely so the published total cannot be used to
 *     recover it. Printing "total less published" here would hand that back, so
 *     the panel says the published rows do not add to the total and stops.
 *  3. INVENT AN EXECUTIVE. The executive role list IS published: these are
 *     public office-holders, named on the organisation chart. It is rendered
 *     exactly as served, no additions — and a band the list names in full is
 *     published for the same reason.
 *
 * Not a recharts panel — band rows and function rows on the house BarRow grid,
 * where a withheld row can carry its own hatched track and its own tooltip.
 */
import React, { useMemo, type ReactNode } from 'react';
import { MetricTile, TileRow } from './MetricTile';
import { PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
import {
  ACCENT,
  INK,
  Def,
  fmtNum,
  panelDateLabel,
  readExtras,
  type DefLookup,
  type WorkforceBasis,
  type WorkforcePanels,
} from './apiContract';

interface Props {
  panels: WorkforcePanels | null;
  /** Part of the shared panel signature. Key-person risk is counted on the
   *  establishment and is the same on every basis — which is what the window
   *  line states. */
  basis: WorkforceBasis;
  loading: boolean;
  def: DefLookup;
}

/** A count. `null` means WITHHELD under the small-group rule — never zero. */
type Cell = number | null;

// =============================================================================
// PALETTE — hard-coded hex, same family as apiContract CATEGORY_COLORS.
// =============================================================================

const BAND_BAR = INK; // #15305D
const FUNCTION_BAR = ACCENT; // #2E6FD9
const TRACK = '#EEF1F6';
const GRADE_INK = '#3D4A60';
const GRADE_BG = '#EEF2F8';

/** Hatched, so a withheld row can never be mistaken for an empty one. */
const WITHHELD_FILL = 'repeating-linear-gradient(45deg, #C3CDDD 0 3px, #EDF1F7 3px 6px)';

/** Senior first: the top of the house, then down. */
const BAND_DISPLAY_ORDER = ['F', 'E', 'D', 'C', 'B', 'NG'];

const NOT_AVAILABLE = 'Not available for this date';

const orderKeys = (available: string[], preferred: string[]) => [
  ...preferred.filter((k) => available.includes(k)),
  ...available.filter((k) => !preferred.includes(k)),
];

const isWithheld = (v: Cell | undefined) => v === null || v === undefined;

/** "F1" / "E5" / "D2" -> a sortable rank. Unparseable grades sort last, in the
 *  order they were served, rather than being reordered on a guess. */
function gradeRank(grade: string | null | undefined): number {
  const m = /^\s*([A-Za-z])\s*(\d+)?\s*$/.exec((grade ?? '').toString());
  if (!m) return -1;
  const letter = m[1].toUpperCase().charCodeAt(0);
  const level = m[2] ? Number(m[2]) : 0;
  return letter * 100 + level;
}

// =============================================================================
// DEFINITIONS — server definitions win; these fill the keys /metric-definitions
// does not publish yet, so no label on this panel is left without one.
// =============================================================================

const defBody = (label: string, body: string, caveat?: string): ReactNode => (
  <div className="space-y-1.5">
    <p className="font-semibold text-white">{label}</p>
    <p>{body}</p>
    {caveat ? (
      <ul className="list-disc space-y-0.5 pl-3.5 text-[11px] text-amber-200/90">
        <li>{caveat}</li>
      </ul>
    ) : null}
  </div>
);

const SUPPRESSION_NOTE =
  'Withheld to protect small groups: fewer than five, so the count is not shown — a group that small '
  + 'identifies individuals. Where only one count in a group was that small, the next-smallest is withheld '
  + 'with it so the total cannot be used to work it back. The row stays visible; only the number is '
  + 'withheld, and it is not zero.';

const FALLBACK_DEFS: Record<string, ReactNode> = {
  key_person: defBody(
    'Single-incumbent senior roles',
    'Senior roles held by exactly one employee, with no second position carrying the same role behind them. If that person goes, the role goes with them until somebody is recruited or moved into it.',
    'A structural risk list, not a succession plan — the platform holds no succession data.',
  ),
  single_incumbent_roles: defBody(
    'Single-incumbent senior roles',
    'Roles in bands D, E and F held in exactly one filled employee position, with no vacant position of the same role, at the date shown.',
    'Retiring a vacant position of the same role adds that role to this count with nothing having changed for any person.',
  ),
  key_person_band_c: defBody(
    'Band C single-incumbent roles',
    'The same test one band below: band C roles held in exactly one filled employee position.',
    'Shown for context; the data pack does not report it.',
  ),
  key_person_sixty_plus: defBody(
    'Senior employees aged 60 or over',
    'Employees aged 60 or over whose position is in band D or above. A count of people, unlike the role counts on this panel. Shown only when every employee in those bands has a birth date on record.',
    'Age is not a retirement date and implies no intention to leave; it is a planning horizon, nothing more.',
  ),
  key_person_by_band: defBody(
    'By grade band',
    'Single-incumbent senior roles split across the senior grade bands, most senior first. Bands D and above are the senior population used throughout this page.',
    'A band whose count is withheld is still inside the total above it.',
  ),
  key_person_by_function: defBody(
    'Single-incumbent band D roles by function',
    'The band D roles held in exactly one filled employee position, split by Role Master function.',
    'Not the data pack’s "D band by function", which counts every band D employee position, filled and open.',
  ),
  key_person_exec_roles: defBody(
    'Executive roles',
    'Executive and senior leadership roles held by one employee. Shown in full rather than withheld: these are public office-holders, named on the organisation chart.',
  ),
  key_person_grade: defBody('Grade', 'The grade the Role Master holds for the role.'),
  suppression: <p>{SUPPRESSION_NOTE}</p>,
};

/** A withheld value is NOT a zero, and it carries the rule that withheld it. */
const Withheld: React.FC<{ def: DefLookup }> = ({ def }) => (
  <span className="inline-flex items-center justify-end text-gray-400">
    —
    <Def def={def} k="suppression" text={SUPPRESSION_NOTE} />
  </span>
);

// =============================================================================
// RISK ROW — label / bar / value, with a hatched track for a withheld row.
//
// The house BarRow renders a null as an empty track, which at a glance is
// indistinguishable from a bar of length zero. On this panel the withheld rows
// are half the story, so they get the equity panel's hatch instead.
// =============================================================================

const RiskRow: React.FC<{
  label: ReactNode;
  caption?: ReactNode;
  value: Cell;
  max: number;
  color: string;
  def: DefLookup;
  withheldTitle: string;
}> = ({ label, caption, value, max, color, def, withheldTitle }) => {
  const withheld = isWithheld(value);
  const pct = !withheld && max > 0 ? Math.max(0.8, ((value as number) / max) * 100) : 0;
  return (
    <div
      className="grid items-center gap-2.5 py-[3px] text-[12.5px]"
      style={{ gridTemplateColumns: 'minmax(0, 190px) 1fr 78px' }}
    >
      <div className="flex min-w-0 items-baseline gap-1.5">
        <span className="truncate text-gray-700">{label}</span>
        {caption ? (
          <span className="shrink-0 text-[10px] uppercase tracking-wide text-gray-400">
            {caption}
          </span>
        ) : null}
      </div>
      <div className="relative h-[13px] overflow-hidden rounded-sm" style={{ background: TRACK }}>
        {withheld ? (
          <span
            role="img"
            aria-label={withheldTitle}
            title={withheldTitle}
            className="absolute inset-0"
            style={{ background: WITHHELD_FILL }}
          />
        ) : (
          <span
            className="absolute inset-y-0 left-0 rounded-sm transition-[width] duration-300"
            style={{ width: `${pct}%`, background: color }}
          />
        )}
      </div>
      <div className="text-right font-semibold tabular-nums text-heritage-blue">
        {withheld ? <Withheld def={def} /> : fmtNum(value)}
      </div>
    </div>
  );
};

// =============================================================================
// PANEL
// =============================================================================

// `basis` is part of the shared panel signature and is deliberately not read:
// the same figures are served on all three bases, which the window line states
// rather than leaving the reader to infer it from a static number.
export const KeyPersonPanel: React.FC<Props> = ({ panels, loading, def }) => {
  const defs = useMemo<DefLookup>(() => (k: string) => def(k) ?? FALLBACK_DEFS[k], [def]);
  const block = readExtras(panels, 'key_person');

  // ---- tiles --------------------------------------------------------------
  // `count` and `band_c_single` are never withheld; a missing one was not
  // carried for this date.
  const count = block?.count;
  const bandC = block?.band_c_single;

  // 60+: withheld (fewer than five) and not available (a senior employee has no
  // birth date on record) are different statements, told apart by the server's
  // own flag — a null alone says neither.
  let sixtyValue: ReactNode = fmtNum(block?.sixty_plus_seniors);
  let sixtySub: ReactNode = 'People, not roles — a planning horizon';
  if (block?.sixty_plus_withheld) {
    sixtyValue = <Withheld def={defs} />;
    sixtySub = 'Fewer than five — withheld to protect small groups';
  } else if (typeof block?.sixty_plus_seniors !== 'number') {
    const basis = block?.sixty_plus_basis;
    sixtyValue = '—';
    sixtySub =
      basis && basis.age_recorded < basis.senior_employees
        ? `${NOT_AVAILABLE}: birth dates are recorded for ${fmtNum(basis.age_recorded)} of ${fmtNum(basis.senior_employees)} employees in bands D and above`
        : NOT_AVAILABLE;
  }

  // ---- bands --------------------------------------------------------------
  const bandRows = useMemo(() => {
    const byBand = block?.by_band ?? {};
    const keys = orderKeys(Object.keys(byBand), BAND_DISPLAY_ORDER);
    return keys.map((k) => ({ band: k, value: byBand[k] ?? null }));
  }, [block]);
  // Maximum over PUBLISHED values only — a null never reaches Math.max.
  const bandMax = Math.max(
    0,
    ...bandRows.map((r) => r.value).filter((v): v is number => typeof v === 'number'),
  );
  const bandsWithheld = bandRows.filter((r) => isWithheld(r.value)).length;

  // ---- functions ----------------------------------------------------------
  // Published rows descending, withheld rows last and alphabetical — the same
  // rule the server applies to its own breakdowns, so the ordering of a
  // withheld row leaks nothing about its size.
  const functionRows = useMemo(() => {
    const rows = (block?.d_by_function ?? []).map((r) => ({
      name: r.function || '—',
      value: r.roles ?? null,
      suppressed: r.suppressed === true || isWithheld(r.roles),
    }));
    const published = rows
      .filter((r) => !r.suppressed)
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
    const withheld = rows.filter((r) => r.suppressed).sort((a, b) => a.name.localeCompare(b.name));
    return [...published, ...withheld];
  }, [block]);
  const functionMax = Math.max(
    0,
    ...functionRows.map((r) => r.value).filter((v): v is number => typeof v === 'number'),
  );
  const functionsWithheld = functionRows.filter((r) => r.suppressed).length;

  // ---- executive roles ----------------------------------------------------
  const execRoles = useMemo(() => {
    const rows = (block?.exec_roles ?? []).map((r, i) => ({
      title: r.title || '—',
      grade: r.grade ?? '',
      rank: gradeRank(r.grade),
      seq: i,
    }));
    // Most senior first; anything whose grade cannot be read keeps its served
    // order at the end rather than being reordered on a guess.
    return rows.sort((a, b) => b.rank - a.rank || a.seq - b.seq);
  }, [block]);

  const rowsWithheld = bandsWithheld + functionsWithheld;

  return (
    <PanelShell
      title="Single-incumbent senior roles"
      defKey="key_person"
      def={defs}
      window={`${panelDateLabel(panels, 'key_person')} — roles and positions as they stand, not a window. It does not follow the basis toggle.`}
      footnote={
        <>
          <b>A structural risk list, not a succession plan.</b> Every row says only that a role
          currently rests on one employee — nothing here says whether a successor exists, is ready,
          or has ever been named. The platform holds no succession data, so that judgement lives
          outside this system entirely.{' '}
          {rowsWithheld ? (
            <>
              {rowsWithheld} {rowsWithheld === 1 ? 'row is' : 'rows are'} withheld to protect small
              groups and drawn as a hatched track, so the rows shown do not add up to the totals
              above them. The difference is deliberately not printed: where only one count was small
              the next-smallest is withheld with it, so the total cannot be used to work it back.{' '}
            </>
          ) : null}
          Grades are read from the Role Master as it stands today; the data pack of September 2026
          counted single-incumbent roles on the grades of 31 July. Retiring a vacant position of
          the same role adds that role to this list with nothing having changed for any person. The
          senior-employee age figure counts people; every other figure on this panel counts roles.
        </>
      }
    >
      {loading && !panels ? (
        <PanelSkeleton height={380} />
      ) : !panels ? (
        <PanelUnavailable what="The single-incumbent role view" />
      ) : !block ? (
        <div className="rounded border border-gray-100 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-500">
          Single-incumbent senior roles are not available for this date.
        </div>
      ) : (
        <div className="space-y-5">
          {/* ── the risk, in three numbers ───────────────────────────────── */}
          <TileRow min="185px">
            <MetricTile
              label="Single-incumbent senior roles"
              value={fmtNum(count)}
              defKey="single_incumbent_roles"
              def={defs}
              sub={typeof count === 'number' ? 'Senior roles resting on one employee' : NOT_AVAILABLE}
            />
            <MetricTile
              label="Band C singles"
              value={fmtNum(bandC)}
              defKey="key_person_band_c"
              def={defs}
              sub={typeof bandC === 'number' ? 'One band below the senior population' : NOT_AVAILABLE}
            />
            <MetricTile
              label="Seniors aged 60+"
              value={sixtyValue}
              defKey="key_person_sixty_plus"
              def={defs}
              sub={sixtySub}
            />
          </TileRow>

          {/* ── by grade band ───────────────────────────────────────────── */}
          {bandRows.length ? (
            <div>
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                <div className="flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  By grade band — most senior first
                  <Def def={defs} k="key_person_by_band" />
                </div>
                {bandsWithheld ? (
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-sm"
                      style={{ background: WITHHELD_FILL }}
                    />
                    withheld
                    <Def def={defs} k="suppression" text={SUPPRESSION_NOTE} />
                  </span>
                ) : null}
              </div>
              {bandRows.map((r) => (
                <RiskRow
                  key={r.band}
                  label={`Band ${r.band}`}
                  value={r.value}
                  max={bandMax}
                  color={BAND_BAR}
                  def={defs}
                  withheldTitle={`Band ${r.band}: withheld to protect small groups`}
                />
              ))}
            </div>
          ) : null}

          {/* ── band D by function ──────────────────────────────────────── */}
          {functionRows.length ? (
            <div>
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                <div className="flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  Single-incumbent band D roles by function
                  <Def def={defs} k="key_person_by_function" />
                </div>
                {functionsWithheld ? (
                  <span className="text-[11px] text-gray-500">
                    {functionsWithheld} of {functionRows.length} functions withheld
                  </span>
                ) : null}
              </div>
              {functionRows.map((r) => (
                <RiskRow
                  key={r.name}
                  label={r.name}
                  value={r.value}
                  max={functionMax}
                  color={FUNCTION_BAR}
                  def={defs}
                  withheldTitle={`${r.name}: withheld to protect small groups`}
                />
              ))}
            </div>
          ) : null}

          {/* ── the executive roles themselves ──────────────────────────── */}
          {execRoles.length ? (
            <div>
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                <div className="flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  Executive roles held by one employee
                  <Def def={defs} k="key_person_exec_roles" />
                </div>
                <span className="text-[11px] text-gray-500">
                  {execRoles.length} roles, shown in full
                </span>
              </div>
              <div className="grid gap-x-6 gap-y-0 sm:grid-cols-2">
                {execRoles.map((r) => (
                  <div
                    key={`${r.grade}-${r.title}`}
                    className="flex items-baseline justify-between gap-2 border-b border-gray-100 py-1 text-[12.5px] last:border-0"
                  >
                    <span className="min-w-0 truncate text-gray-700" title={r.title}>
                      {r.title}
                    </span>
                    <span
                      className="shrink-0 rounded px-1.5 py-[1px] text-[10.5px] font-semibold tabular-nums"
                      style={{ color: GRADE_INK, background: GRADE_BG }}
                      title={`Grade ${r.grade}`}
                    >
                      {r.grade || '—'}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-1.5 text-[11px] leading-snug text-gray-500">
                These posts are public office-holders named on the organisation chart, so they are
                shown in full rather than withheld. Listing a role here says nothing about the person
                holding it.
              </div>
            </div>
          ) : null}
        </div>
      )}
    </PanelShell>
  );
};
